import React, { useEffect, useState } from "react";
import { Button, Col, RSelect } from "../../../components/Component";
import Head from "../../../layout/head/Head";
import { Form } from "reactstrap";
import { handleGetApis, handlePostApis } from "../../../service/Api";
import Content from "../../../layout/content/Content";
import { filterStatus } from "../../../service/Data";
import { useNavigate, useLocation } from "react-router-dom";

// 0 is a valid price/stock (e.g. sold out), so only truly empty values fail.
const isEmpty = (val) => val === undefined || val === null || String(val).trim() === "";

/* ─── Reusable section card wrapper ──────────────────────── */
const SectionCard = ({ icon, title, color, onAdd, addLabel, children }) => (
  <div
    style={{
      border: `1.5px solid ${color}22`,
      borderRadius: 8,
      marginBottom: 20,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        background: `${color}11`,
        borderBottom: `1.5px solid ${color}22`,
        padding: "10px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13, color }}>
        {icon} {title}
      </span>
      <Button size="sm" color="primary" type="button" onClick={onAdd}>
        + {addLabel}
      </Button>
    </div>
    <div style={{ padding: "12px 16px" }}>{children}</div>
  </div>
);

const EditProduct = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const productId = new URLSearchParams(search).get("id");
  const [categoryList, setCategoryData] = useState([]);
  const [errors, setErrors] = useState({});
  const [addData, setAddData] = useState({
    name: "",
    title: "",
    slug: "",
    short_description: "",
    description: "",
    category_id: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    is_active: true,
    is_featured: false,
    is_cart: false,
    variants: [],
    fragrance_notes: {
      top:   [{ note_name: "", sort_order: 0 }],
      heart: [{ note_name: "", sort_order: 0 }],
      base:  [{ note_name: "", sort_order: 0 }],
    },
    badges:   [{ badge_title: "", badge_icon: "", sort_order: 0 }],
    faqs:     [{ question: "", answer: "", sort_order: 0 }],
    features: [{ title: "", value: "", sort_order: 0 }],
    policies: [{ policy_type: "shipping", description: "", sort_order: 0 }],
  });

  /* ── Generic list helpers ────────────────────────────────── */
  const addItem    = (key, blank) => setAddData((p) => ({ ...p, [key]: [...p[key], blank] }));
  const removeItem = (key, idx)  => setAddData((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));
  const updateItem = (key, idx, field, val) =>
    setAddData((p) => {
      const arr = [...p[key]];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, [key]: arr };
    });

  /* ── Fragrance Notes helpers ─────────────────────────────── */
  const addNote    = (type)           => setAddData((p) => ({ ...p, fragrance_notes: { ...p.fragrance_notes, [type]: [...p.fragrance_notes[type], { note_name: "", sort_order: 0 }] } }));
  const removeNote = (type, idx)      => setAddData((p) => ({ ...p, fragrance_notes: { ...p.fragrance_notes, [type]: p.fragrance_notes[type].filter((_, i) => i !== idx) } }));
  const updateNote = (type, idx, f, v) =>
    setAddData((p) => {
      const notes = [...p.fragrance_notes[type]];
      notes[idx] = { ...notes[idx], [f]: v };
      return { ...p, fragrance_notes: { ...p.fragrance_notes, [type]: notes } };
    });

  /* ── Fetch category ──────────────────────────────────────── */
  const fetchCategory = async () => {
    const res = await handleGetApis("admin/get/categories");
    if (res?.data?.result) {
      setCategoryData(res.data.result.filter((c) => c.is_active === 1).map((c) => ({ value: c.id, label: c.name })));
    }
  };

  /* ── Fetch product ───────────────────────────────────────── */
  const fetchProduct = async () => {
    const res = await handleGetApis(`admin/get/product/${productId}`);
    if (res?.data?.success) {
      const p = res.data.data;
console.log(p)
      const rawNotes = p.fragrance_notes || [];
      const groupNotes = (type) => {
        const filtered = rawNotes
          .filter((n) => n.note_type === type)
          .map((n) => ({ id: n.id, note_name: n.note_name, sort_order: n.sort_order ?? 0 }));
        return filtered.length ? filtered : [{ note_name: "", sort_order: 0 }];
      };

      const mapList = (arr, fields) =>
        arr?.length ? arr.map((r) => ({ id: r.id, ...Object.fromEntries(fields.map((f) => [f, r[f] ?? ""])), sort_order: r.sort_order ?? 0 }))
                    : [Object.fromEntries([...fields.map((f) => [f, ""]), ["sort_order", 0]])];

      setAddData({
        name:              p.name,
        title:             p.title,
        slug:              p.slug,
        short_description: p.short_description,
        description:       p.description,
        category_id:       p.category_id,
        meta_title:        p.meta_title || "",
        meta_description:  p.meta_description || "",
        meta_keywords:     p.meta_keywords || "",
        is_active:         p.is_active === 1,
        is_featured:       p.is_featured === 1,
        is_cart:           p.is_cart === 1,
        variants:          (p.variants ?? []).map((v) => ({ ...v, is_active: v.is_active === 1 })),
        fragrance_notes: {
          top:   groupNotes("top"),
          heart: groupNotes("heart"),
          base:  groupNotes("base"),
        },
        badges:   mapList(p.badges,   ["badge_title", "badge_icon"]),
        faqs:     mapList(p.faqs,     ["question", "answer"]),
        features: mapList(p.features, ["title", "value"]),
        policies: mapList(p.policies, ["policy_type", "description"]),
      });
    }
  };

  useEffect(() => {
    fetchCategory();
    fetchProduct();
  }, []);

  /* ── Validation ──────────────────────────────────────────── */
  const validateForm = () => {
    let err = {};
    if (!addData.name)        err.name        = "Name required";
    if (!addData.title)       err.title       = "Title required";
    if (!addData.slug)        err.slug        = "Slug required";
    if (!addData.category_id) err.category_id = "Category required";

    addData.variants.forEach((v, i) => {
      if (isEmpty(v.size))  err[`size_${i}`]  = "Size required";
      if (isEmpty(v.price)) err[`price_${i}`] = "Price required";
      if (isEmpty(v.mrp))   err[`mrp_${i}`]   = "MRP required";
      if (isEmpty(v.stock)) err[`stock_${i}`] = "Stock required";
    });

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ── Update ──────────────────────────────────────────────── */
  const updateProduct = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // fragrance notes
    const allNotes = [
      ...addData.fragrance_notes.top.filter((n) => n.note_name.trim()).map((n) => ({ ...n, note_type: "top" })),
      ...addData.fragrance_notes.heart.filter((n) => n.note_name.trim()).map((n) => ({ ...n, note_type: "heart" })),
      ...addData.fragrance_notes.base.filter((n) => n.note_name.trim()).map((n) => ({ ...n, note_type: "base" })),
    ];

    const payload = {
      product_id:        productId,
      name:              addData.name,
      title:             addData.title,
      slug:              addData.slug,
      short_description: addData.short_description,
      description:       addData.description,
      category_id:       addData.category_id,
      meta_title:        addData.meta_title || "",
      meta_description:  addData.meta_description || "",
      meta_keywords:     addData.meta_keywords || "",
      is_active:         addData.is_active ? 1 : 0,
      is_featured:       addData.is_featured ? 1 : 0,
      is_cart:           addData.is_cart ? 1 : 0,
      variants:          JSON.stringify(addData.variants),
      fragrance_notes:   JSON.stringify(allNotes),
      badges:            JSON.stringify(addData.badges.filter((b) => b.badge_title.trim())),
      faqs:              JSON.stringify(addData.faqs.filter((f) => f.question.trim())),
      features:          JSON.stringify(addData.features.filter((f) => f.title.trim())),
      policies:          JSON.stringify(addData.policies.filter((p) => p.description.trim())),
    };

    const res = await handlePostApis("admin/update/product", payload);
    if (res?.data?.success) {
      alert("Product updated");
      navigate("/product-list");
    } else {
      alert(res?.data?.message || "Failed to update product");
    }
  };
  /* ── UI ──────────────────────────────────────────────────── */
  return (
    <>
      <Head title="Edit Product" />
      <Content>
        <h5>Edit Product</h5>

        <Form className="row gy-4 mt-3" onSubmit={updateProduct}>

          {/* ── Basic Fields ── */}
          <Col md="6">
            <label className="form-label">Name</label>
            <input
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              value={addData.name}
              // The slug is the live URL: renaming must not silently change it (breaks
              // links and SEO). Edit the slug field itself to change the URL.
              onChange={(e) => setAddData({ ...addData, name: e.target.value })}
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </Col>

          <Col md="6">
            <label className="form-label">Title</label>
            <input
              className={`form-control ${errors.title ? "is-invalid" : ""}`}
              value={addData.title}
              onChange={(e) => setAddData({ ...addData, title: e.target.value })}
            />
            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
          </Col>

          <Col md="6">
            <label className="form-label">Slug</label>
            <input
              className={`form-control ${errors.slug ? "is-invalid" : ""}`}
              value={addData.slug}
              onChange={(e) => setAddData({ ...addData, slug: e.target.value })}
            />
            {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
          </Col>

          <Col md="6">
            <label className="form-label">Category</label>
            <RSelect
              options={categoryList}
              value={categoryList.find((c) => c.value === addData.category_id)}
              onChange={(e) => setAddData({ ...addData, category_id: e.value })}
            />
            {errors.category_id && <div className="text-danger small mt-1">{errors.category_id}</div>}
          </Col>

          <Col md="12">
            <label className="form-label">Short Description</label>
            <textarea
              className="form-control"
              rows={2}
              value={addData.short_description}
              onChange={(e) => setAddData({ ...addData, short_description: e.target.value })}
            />
          </Col>

          <Col md="12">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={4}
              value={addData.description}
              onChange={(e) => setAddData({ ...addData, description: e.target.value })}
            />
          </Col>

          {/* ── SEO / Meta Fields ── */}
          <Col md="6">
            <label className="form-label">Meta Title</label>
            <input
              className="form-control"
              placeholder="Meta title for SEO"
              value={addData.meta_title}
              onChange={(e) => setAddData({ ...addData, meta_title: e.target.value })}
            />
          </Col>

          <Col md="6">
            <label className="form-label">Meta Keywords</label>
            <input
              className="form-control"
              placeholder="Keywords separated by comma"
              value={addData.meta_keywords}
              onChange={(e) => setAddData({ ...addData, meta_keywords: e.target.value })}
            />
          </Col>

          <Col md="12">
            <label className="form-label">Meta Description</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Meta description for search engines..."
              value={addData.meta_description}
              onChange={(e) => setAddData({ ...addData, meta_description: e.target.value })}
            />
          </Col>

          {/* ── Variants ── */}
          {addData.variants.map((v, index) => (
            <div key={index} className="row mt-2 mb-1 align-items-end">
              <Col md="2">
                <label className="form-label small mb-1">Size</label>
                <input placeholder="Size" className="form-control" value={v.size}
                  onChange={(e) => updateItem("variants", index, "size", e.target.value)} />
                {errors[`size_${index}`] && <small className="text-danger">{errors[`size_${index}`]}</small>}
              </Col>
              <Col md="2">
                <label className="form-label small mb-1">MRP</label>
                <input placeholder="MRP" className="form-control" value={v.mrp}
                  onChange={(e) => updateItem("variants", index, "mrp", e.target.value)} />
                {errors[`mrp_${index}`] && <small className="text-danger">{errors[`mrp_${index}`]}</small>}
              </Col>
              <Col md="2">
                <label className="form-label small mb-1">Price</label>
                <input placeholder="Price" className="form-control" value={v.price}
                  onChange={(e) => updateItem("variants", index, "price", e.target.value)} />
                {errors[`price_${index}`] && <small className="text-danger">{errors[`price_${index}`]}</small>}
              </Col>
              <Col md="2">
                <label className="form-label small mb-1">Stock</label>
                <input placeholder="Stock" className="form-control" value={v.stock}
                  onChange={(e) => updateItem("variants", index, "stock", e.target.value)} />
                {errors[`stock_${index}`] && <small className="text-danger">{errors[`stock_${index}`]}</small>}
              </Col>
              <Col md="2">
                <label className="form-label small mb-1">Status</label>
                <RSelect options={filterStatus} value={{ value: v.is_active, label: v.is_active ? "Active" : "Not Active" }}
                  onChange={(e) => updateItem("variants", index, "is_active", e.value)} />
              </Col>
              <Col md="2">
                <Button type="button" color="danger"
                  onClick={() => { if (addData.variants.length === 1) return; setAddData({ ...addData, variants: addData.variants.filter((_, i) => i !== index) }); }}>
                  Remove
                </Button>
              </Col>
            </div>
          ))}
          <Col className="mb-2">
            <Button color="primary" type="button"
              onClick={() => setAddData({ ...addData, variants: [...addData.variants, { size: "", mrp: "", price: "", stock: "", is_active: true }] })}>
              + Add Variant
            </Button>
          </Col>


          {/* ── Status toggles ── */}
          <Col md="3">
            <label className="form-label">Feature Product</label>
            <RSelect options={filterStatus} value={{ value: addData.is_featured, label: addData.is_featured ? "Active" : "Not Active" }}
              onChange={(e) => setAddData({ ...addData, is_featured: e.value })} />
          </Col>
          <Col md="3">
            <label className="form-label">Cart Product</label>
            <RSelect options={filterStatus} value={{ value: addData.is_cart, label: addData.is_cart ? "Active" : "Not Active" }}
              onChange={(e) => setAddData({ ...addData, is_cart: e.value })} />
          </Col>
          <Col md="6">
            <label className="form-label">Status</label>
            <RSelect options={filterStatus} value={{ value: addData.is_active, label: addData.is_active ? "Active" : "Not Active" }}
              onChange={(e) => setAddData({ ...addData, is_active: e.value })} />
          </Col>

          {/* ══════════════════════════════════════════════════════════
              SECTION: Fragrance Notes
          ══════════════════════════════════════════════════════════ */}
          <Col md="12">
            <h6 className="title mt-2 mb-3" style={{ borderBottom: "2px solid #e5e9f2", paddingBottom: 8 }}>
              🌸 Fragrance Notes
            </h6>
            {[
              { type: "top",   icon: "🌿", label: "Top Notes",   color: "#1ee0ac" },
              { type: "heart", icon: "❤️", label: "Heart Notes", color: "#e85347" },
              { type: "base",  icon: "🪨", label: "Base Notes",  color: "#816bff" },
            ].map(({ type, icon, label, color }) => (
              <SectionCard key={type} icon={icon} title={label} color={color} onAdd={() => addNote(type)} addLabel="Add Note">
                {addData.fragrance_notes[type].map((note, idx) => (
                  <div key={idx} className="row mb-2 align-items-center">
                    <Col md="7">
                      <input className="form-control form-control-sm" placeholder="Note name (e.g. Rose, Sandalwood)"
                        value={note.note_name} onChange={(e) => updateNote(type, idx, "note_name", e.target.value)} />
                    </Col>
                    <Col md="3">
                      <input className="form-control form-control-sm" placeholder="Sort order" type="number"
                        value={note.sort_order} onChange={(e) => updateNote(type, idx, "sort_order", Number(e.target.value))} />
                    </Col>
                    <Col md="2">
                      <Button size="sm" color="danger" type="button"
                        disabled={addData.fragrance_notes[type].length === 1}
                        onClick={() => removeNote(type, idx)}>
                        Remove
                      </Button>
                    </Col>
                  </div>
                ))}
              </SectionCard>
            ))}
          </Col>

          {/* ══════════════════════════════════════════════════════════
              SECTION: Badges
          ══════════════════════════════════════════════════════════ */}
          <Col md="12">
            <h6 className="title mt-2 mb-3" style={{ borderBottom: "2px solid #e5e9f2", paddingBottom: 8 }}>
              🏷️ Product Badges
            </h6>
            <SectionCard icon="🏷️" title="Badges" color="#f4a261" onAdd={() => addItem("badges", { badge_title: "", badge_icon: "", sort_order: 0 })} addLabel="Add Badge">
              {addData.badges.map((b, idx) => (
                <div key={idx} className="row mb-2 align-items-center">
                  <Col md="4">
                    <input className="form-control form-control-sm" placeholder="Badge title (e.g. Bestseller)"
                      value={b.badge_title} onChange={(e) => updateItem("badges", idx, "badge_title", e.target.value)} />
                  </Col>
                  <Col md="4">
                    <input className="form-control form-control-sm" placeholder="Icon (emoji or URL, optional)"
                      value={b.badge_icon} onChange={(e) => updateItem("badges", idx, "badge_icon", e.target.value)} />
                  </Col>
                  <Col md="2">
                    <input className="form-control form-control-sm" placeholder="Order" type="number"
                      value={b.sort_order} onChange={(e) => updateItem("badges", idx, "sort_order", Number(e.target.value))} />
                  </Col>
                  <Col md="2">
                    <Button size="sm" color="danger" type="button"
                      disabled={addData.badges.length === 1}
                      onClick={() => removeItem("badges", idx)}>
                      Remove
                    </Button>
                  </Col>
                </div>
              ))}
            </SectionCard>
          </Col>

          {/* ══════════════════════════════════════════════════════════
              SECTION: FAQs
          ══════════════════════════════════════════════════════════ */}
          <Col md="12">
            <h6 className="title mt-2 mb-3" style={{ borderBottom: "2px solid #e5e9f2", paddingBottom: 8 }}>
              ❓ Product FAQs
            </h6>
            <SectionCard icon="❓" title="FAQs" color="#2196f3" onAdd={() => addItem("faqs", { question: "", answer: "", sort_order: 0 })} addLabel="Add FAQ">
              {addData.faqs.map((faq, idx) => (
                <div key={idx} className="mb-3 p-3" style={{ background: "#f8f9ff", borderRadius: 6, border: "1px solid #e5e9f2" }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#526484" }}>FAQ #{idx + 1}</span>
                    <div className="d-flex align-items-center gap-2">
                      <input className="form-control form-control-sm" placeholder="Order" type="number"
                        style={{ width: 80 }}
                        value={faq.sort_order} onChange={(e) => updateItem("faqs", idx, "sort_order", Number(e.target.value))} />
                      <Button size="sm" color="danger" type="button"
                        disabled={addData.faqs.length === 1}
                        onClick={() => removeItem("faqs", idx)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                  <input className="form-control form-control-sm mb-2" placeholder="Question"
                    value={faq.question} onChange={(e) => updateItem("faqs", idx, "question", e.target.value)} />
                  <textarea className="form-control form-control-sm" placeholder="Answer" rows={2}
                    value={faq.answer} onChange={(e) => updateItem("faqs", idx, "answer", e.target.value)} />
                </div>
              ))}
            </SectionCard>
          </Col>

          {/* ══════════════════════════════════════════════════════════
              SECTION: Features
          ══════════════════════════════════════════════════════════ */}
          <Col md="12">
            <h6 className="title mt-2 mb-3" style={{ borderBottom: "2px solid #e5e9f2", paddingBottom: 8 }}>
              ✨ Product Features
            </h6>
            <SectionCard icon="✨" title="Features" color="#8b5cf6" onAdd={() => addItem("features", { title: "", value: "", sort_order: 0 })} addLabel="Add Feature">
              {addData.features.map((feat, idx) => (
                <div key={idx} className="row mb-2 align-items-center">
                  <Col md="4">
                    <input className="form-control form-control-sm" placeholder="Title (e.g. Longevity)"
                      value={feat.title} onChange={(e) => updateItem("features", idx, "title", e.target.value)} />
                  </Col>
                  <Col md="4">
                    <input className="form-control form-control-sm" placeholder="Value (e.g. 8-10 hours)"
                      value={feat.value} onChange={(e) => updateItem("features", idx, "value", e.target.value)} />
                  </Col>
                  <Col md="2">
                    <input className="form-control form-control-sm" placeholder="Order" type="number"
                      value={feat.sort_order} onChange={(e) => updateItem("features", idx, "sort_order", Number(e.target.value))} />
                  </Col>
                  <Col md="2">
                    <Button size="sm" color="danger" type="button"
                      disabled={addData.features.length === 1}
                      onClick={() => removeItem("features", idx)}>
                      Remove
                    </Button>
                  </Col>
                </div>
              ))}
            </SectionCard>
          </Col>

          {/* ══════════════════════════════════════════════════════════
              SECTION: Policies
          ══════════════════════════════════════════════════════════ */}
          <Col md="12">
            <h6 className="title mt-2 mb-3" style={{ borderBottom: "2px solid #e5e9f2", paddingBottom: 8 }}>
              📋 Product Policies
            </h6>
            <SectionCard
              icon="📋" title="Policies" color="#d97706"
              onAdd={() => addItem("policies", { policy_type: "shipping", description: "", sort_order: 0 })}
              addLabel="Add Policy"
            >
              {addData.policies.map((pol, idx) => (
                <div key={idx} className="mb-3 p-3" style={{ background: "#fffbf0", borderRadius: 6, border: "1px solid #fde68a" }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>Policy #{idx + 1}</span>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Order" type="number"
                        style={{ width: 80 }}
                        value={pol.sort_order}
                        onChange={(e) => updateItem("policies", idx, "sort_order", Number(e.target.value))}
                      />
                      <Button size="sm" color="danger" type="button"
                        disabled={addData.policies.length === 1}
                        onClick={() => removeItem("policies", idx)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="row align-items-start">
                    <Col md="3">
                      <label className="form-label small mb-1">Policy Type</label>
                      <select
                        className="form-select form-select-sm"
                        value={pol.policy_type}
                        onChange={(e) => updateItem("policies", idx, "policy_type", e.target.value)}
                      >
                        <option value="shipping">🚚 Shipping</option>
                        <option value="return">↩️ Return</option>
                        <option value="refund">💰 Refund</option>
                        <option value="authenticity">✅ Authenticity</option>
                      </select>
                    </Col>
                    <Col md="9">
                      <label className="form-label small mb-1">Description</label>
                      <textarea
                        className="form-control form-control-sm"
                        placeholder="Describe the policy..."
                        rows={3}
                        value={pol.description}
                        onChange={(e) => updateItem("policies", idx, "description", e.target.value)}
                      />
                    </Col>
                  </div>
                </div>
              ))}
            </SectionCard>
          </Col>

          {/* ── Submit ── */}
          <Col md="12" className="mt-2">
            <Button type="submit" color="primary" size="lg">
              Update Product
            </Button>
          </Col>

        </Form>
      </Content>
    </>
  );
};

export default EditProduct;
